import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { computeService, breakEven } from "@/lib/calc";
import { SectionCard, SectionTitle, InfoBox } from "@/components/ui-kit";
import { GuidedTip } from "@/components/GuidedTip";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const inputClass = "bg-brand-ivory/50 border-brand-line rounded-xl h-auto py-3 focus-visible:ring-2 focus-visible:ring-brand-champagne";

const PRESETS = {
  it: ["Affitto / postazione", "Elettricità", "Acqua", "Internet / telefono", "Software", "Assicurazione", "Marketing", "Formazione", "Commercialista"],
  es: ["Alquiler / puesto", "Electricidad", "Agua", "Internet / teléfono", "Software", "Seguro", "Marketing", "Formación", "Gestoría"],
  en: ["Rent / workstation", "Electricity", "Water", "Internet / phone", "Software", "Insurance", "Marketing", "Training", "Accountant"],
};

export default function FixedCostsPage() {
  const { t, user, fmt, lang } = useAuth();
  const qc = useQueryClient();
  const { data: costs = [] } = useQuery({ queryKey: ["fixed-costs"], queryFn: () => api.get("/fixed-costs").then((r) => r.data) });
  const { data: services = [] } = useQuery({ queryKey: ["services"], queryFn: () => api.get("/services").then((r) => r.data) });

  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [selectedService, setSelectedService] = useState("");

  const total = costs.reduce((s, c) => s + Number(c.monthly_amount || 0), 0);

  const add = async () => {
    if (!name.trim()) return;
    await api.post("/fixed-costs", { name: name.trim(), monthly_amount: Number(amount) || 0 });
    setName(""); setAmount("");
    qc.invalidateQueries({ queryKey: ["fixed-costs"] });
    toast.success(t("fixed.saved"));
  };

  const updateAmount = async (c, val) => {
    await api.put(`/fixed-costs/${c.id}`, { name: c.name, monthly_amount: Number(val) || 0 });
    qc.invalidateQueries({ queryKey: ["fixed-costs"] });
  };

  const remove = async (id) => {
    await api.delete(`/fixed-costs/${id}`);
    qc.invalidateQueries({ queryKey: ["fixed-costs"] });
    toast.success(t("fixed.deleted"));
  };

  const svc = services.find((s) => s.id === selectedService);
  const be = svc ? breakEven(computeService(svc, user), total) : null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-3xl sm:text-4xl font-medium text-brand-anthracite">{t("fixed.title")}</h1>
        <p className="mt-1 text-brand-taupe">{t("fixed.subtitle")}</p>
      </div>

      <GuidedTip id="fixed-costs">{t("tips.fixedCosts")}</GuidedTip>

      <div className="grid lg:grid-cols-2 gap-6">
        <SectionCard>
          <SectionTitle>{t("fixed.add")}</SectionTitle>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 space-y-1"><Label className="text-xs">{t("fixed.name")}</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("fixed.namePlaceholder")} className={inputClass} data-testid="fixed-name-input" /></div>
            <div className="w-32 space-y-1"><Label className="text-xs">{t("fixed.amount")}</Label>
              <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-taupe">{user.currency === "GBP" ? "£" : "€"}</span>
                <Input type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} className={`${inputClass} pl-8`} data-testid="fixed-amount-input" /></div>
            </div>
            <Button onClick={add} data-testid="fixed-add-button" className="rounded-xl bg-brand-anthracite text-brand-ivory sm:self-end h-12"><Plus className="h-5 w-5" /></Button>
          </div>
          <div className="mt-4">
            <div className="text-xs uppercase tracking-wider text-brand-taupe mb-2">{t("fixed.presetTitle")}</div>
            <div className="flex flex-wrap gap-2">
              {(PRESETS[lang] || PRESETS.it).map((p) => (
                <button key={p} onClick={() => setName(p)} data-testid={`fixed-preset-${p}`} className="rounded-full border border-brand-line bg-white px-3 py-1 text-xs text-brand-taupe hover:border-brand-taupe hover:text-brand-anthracite">{p}</button>
              ))}
            </div>
          </div>
        </SectionCard>

        <SectionCard accent>
          <SectionTitle>{t("fixed.total")}</SectionTitle>
          {costs.length === 0 ? (
            <div className="py-6 text-center"><p className="font-medium text-brand-anthracite">{t("fixed.empty")}</p><p className="mt-1 text-sm text-brand-taupe">{t("fixed.emptyDesc")}</p></div>
          ) : (
            <>
              <div className="divide-y divide-brand-line" data-testid="fixed-list">
                {costs.map((c) => (
                  <div key={c.id} className="flex items-center justify-between gap-3 py-2.5" data-testid={`fixed-item-${c.id}`}>
                    <span className="flex-1 text-sm text-brand-anthracite">{c.name}</span>
                    <div className="relative w-28"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-taupe text-sm">{user.currency === "GBP" ? "£" : "€"}</span>
                      <Input type="number" min="0" defaultValue={c.monthly_amount} onBlur={(e) => updateAmount(c, e.target.value)} className={`${inputClass} pl-7 py-2 text-right`} data-testid={`fixed-edit-${c.id}`} /></div>
                    <button onClick={() => remove(c.id)} className="rounded-lg p-2 text-brand-taupe hover:bg-[#B85B5B]/10 hover:text-[#B85B5B]" data-testid={`fixed-remove-${c.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-center justify-between border-t border-brand-line pt-4">
                <span className="font-medium">{t("fixed.total")}</span>
                <span className="font-serif text-2xl font-semibold text-brand-anthracite" data-testid="fixed-total">{fmt(total)}</span>
              </div>
            </>
          )}
        </SectionCard>
      </div>

      <SectionCard>
        <SectionTitle sub={t("fixed.breakEvenDesc")}>{t("fixed.breakEvenTitle")}</SectionTitle>
        <GuidedTip id="break-even" className="mb-4">{t("tips.breakEven")}</GuidedTip>
        {services.length === 0 ? (
          <p className="text-sm text-brand-taupe">{t("fixed.needServices")}</p>
        ) : (
          <div className="space-y-4">
            <Select value={selectedService} onValueChange={setSelectedService}>
              <SelectTrigger className={`${inputClass} max-w-sm`} data-testid="breakeven-select"><SelectValue placeholder={t("fixed.selectService")} /></SelectTrigger>
              <SelectContent className="bg-white">
                {services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {svc && (
              be ? <InfoBox tone="blush" data-testid="breakeven-result">{t("editor.breakEvenNeed", { n: be, total: fmt(total) })}<div className="mt-2 text-xs">{t("editor.breakEvenHelp")}</div></InfoBox>
                 : <InfoBox tone="warning">{t("editor.breakEvenImpossible")}</InfoBox>
            )}
          </div>
        )}
      </SectionCard>
    </div>
  );
}
